import Box from '@/common/components/Box'
import ListHeader from '@/common/components/ListHeader'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { ProductData } from '@/common/types/product.type'
import { CardCurrentLimits } from '@/features/cardCurrentLimits/components/cardCurrentLimits'
import { useCardDetails } from '@/features/cards/hooks/useCardDetails'
import type { CompanyData } from '@/features/companies/types/companies.type'
import { useCardApplicableProducts } from '@/features/consumption/hooks/useCardApplicableProducts'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { EquipmentInfo } from '@/features/refund/components/equipmentInfo'
import { FileSearch } from 'lucide-react'
import { useEffect, useState } from 'react'

const CardCurrentLimitsShow = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [cardIdentifier, setCardIdentifier] = useDebounce(filterParams.cardIdentifier || '')
  const [establishment, setEstablishment] = useState<Option | undefined>(
    filterParams.establishmentId && filterParams.establishmentLabel
      ? { value: filterParams.establishmentId, label: filterParams.establishmentLabel }
      : undefined,
  )
  const [product, setProduct] = useState<ProductData | undefined>()
  const [searchEstablishment, setSearchEstablishment] = useDebounce(filterParams.establishmentLabel || '')
  const [searchProduct, setSearchProduct] = useDebounce(filterParams.productDisplayName || '')

  const {
    data: cardDetails,
    isPending: cardDetailsIsPending,
    isError: cardDetailsIsError,
  } = useCardDetails(cardIdentifier)

  const { data: establishmentCompaniesOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer(),
    }),
  })

  const { data: products, isPending: productsIsPending } = useCardApplicableProducts<ProductData[]>({
    filters: {
      cardId: cardDetails?.id,
      establishmentId: establishment?.value,
      search: searchProduct,
    },
  })
  const mapProductToOptions = Mapper.mapToOptions<ProductData>({ valueFieldName: 'id', labelFieldName: 'display_name' })

  useEffect(() => {
    if (filterParams.productId && !productsIsPending && products.length > 0 && !product?.id) {
      const foundProduct = products.find(
        (p) => p.id === filterParams.productId,
      )
      if (foundProduct) {
        setProduct(foundProduct)
      }
    } else {
      setSearchProduct('')
    }
  }, [productsIsPending, filterParams.productId, product?.id])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader Icon={<FileSearch className="w-10 h-10" strokeWidth={1.5} />} title="Consulta" />
      </div>

      <Box className="h-[calc((100vh-104px-1.5rem))] overflow-y-auto flex gap-6">
        <div className="grow w-1/2 flex flex-col gap-6">
          <p className="text-[20px] font-bold">Informações essenciais</p>

          <TextField
            label="Código do cartão ou Placa/N. de série do equipamento"
            value={filterParams.cardIdentifier}
            onChange={(e: any) => {
              const value = e.target.value
              setCardIdentifier(value)
              setEstablishment(undefined)
              setProduct(undefined)
              setFilterParams((params: any) => ({
                ...params,
                cardIdentifier: value,
                establishmentId: undefined,
                establishmentLabel: undefined,
                productId: undefined,
                productDisplayName: undefined,
              }))
            }}
            markAsRequired
            errorMessage={cardDetailsIsError ? 'Cartão não encontrado' : undefined}
            isLoading={!!(cardIdentifier && cardDetailsIsPending)}
          />

          {cardDetails?.id && (
            <>
              <Select
                label="Estabelecimento"
                options={establishmentCompaniesOptions}
                onInputChange={(e) => setSearchEstablishment(e)}
                value={establishment}
                onChange={(e: any) => {
                  setEstablishment(e)
                  setProduct(undefined)
                  setFilterParams((params: any) => ({
                    ...params,
                    establishmentId: e?.value,
                    establishmentLabel: e?.label,
                    productId: undefined,
                    productDisplayName: undefined,
                  }))
                }}
              />

              <Select
                label="Produto(s) para consulta"
                markAsRequired
                options={mapProductToOptions(products)}
                onInputChange={(e) => setSearchProduct(e)}
                value={product ? mapProductToOptions([product])[0] : null}
                onChange={(e: any) => {
                  const selectedProduct = products.find((p) => p.id === e.value)
                  setProduct(selectedProduct)
                  setFilterParams((params: any) => ({
                    ...params,
                    productId: selectedProduct?.id,
                    productDisplayName: selectedProduct?.display_name,
                  }))
                }}
                noOptionsMessage={
                  cardDetails?.id
                    ? () => 'Este estabelecimento não possui produtos autorizados para esta consulta.'
                    : undefined
                }
              />
            </>
          )}

          {cardDetails && (
            <EquipmentInfo cardDetails={cardDetails} companyData={cardDetails.company as unknown as CompanyData} />
          )}
        </div>

        <div className="grow w-1/2 flex flex-col gap-6 overflow-y-auto pr-2">
          {cardIdentifier && product && cardDetails && (
            <CardCurrentLimits
              card={cardIdentifier}
              cardDetails={cardDetails}
              product={product}
              establishmentBranchId={establishment?.value}
            />
          )}
        </div>
      </Box>
    </div>
  )
}

export default CardCurrentLimitsShow
